import { Box, chakra, Flex, Text } from "@chakra-ui/react";
import { isPopulatedArray, toNumberDisplayValue } from "@intentsify/utils";
import { defineChart } from "@tanstack/charts";
import { tooltip } from "@tanstack/charts/tooltip";
import { Fragment, useCallback, useMemo } from "react";
import { useMeasure } from "react-use";
import { Colour } from "theme";
import { byLook } from "theme/look";
import { ErrorBoundary } from "../../ErrorBoundary";
import { Loader } from "../../Loader";
import { NoData } from "../../NoData";
import {
  BrandChart,
  ChartExportSvg,
  LineLegend,
  LineLegendItem,
  useBrandChartTheme,
} from "../BrandChart";
import { MIN_HEIGHT } from "../Charts.const";
import { LegacyTimeseriesNew } from "./legacy/Timeseries";
import {
  buildTimeseriesNewDefinition,
  formatTimeValue,
  getTimeValues,
  MarkerRow,
  MUTED_OPACITY,
  seriesColour,
  TimeseriesNewRow,
  TimeseriesNewSeries,
  toTimeseriesNewSeries,
} from "./Timeseries.definition";
import { TimeseriesProps } from "./Timeseries.types";

export type { TimeseriesProps };

const LEGEND_RIGHT_MIN_WIDTH = 1000;

const isSeriesRow = (datum: unknown): datum is TimeseriesNewRow =>
  typeof datum === "object" && datum !== null && "seriesIndex" in datum;

const isMarkerRow = (datum: unknown): datum is MarkerRow =>
  typeof datum === "object" && datum !== null && "description" in datum;

const BrandTimeseriesNew = ({
  leftAxisBars,
  rightAxisBars,
  leftAxisLines,
  rightAxisLines,
  id,
  title,
  exportMode,
  disableLegend = false,
  legendPosition,
  isLoading = false,
  stretch = false,
  markers = [],
  noDataMessage,
  scale = "linear",
  minHeight = MIN_HEIGHT,
  xAxisTimeGap,
  leftAxisLabel,
  rightAxisLabel,
  barsTooltipRenderer,
  leftAxisLinesTooltipRenderer,
  rightAxisLinesTooltipRenderer,
  customLegend = [],
  skipDecimals,
  xAxisLabels,
  timesScalePadding,
  displayLinesInFrontOfBars,
}: TimeseriesProps) => {
  const brandTheme = useBrandChartTheme();
  const [measureRef, { width: containerWidth }] = useMeasure<HTMLDivElement>();

  const series = useMemo(
    () =>
      toTimeseriesNewSeries({
        leftAxisBars,
        rightAxisBars,
        leftAxisLines,
        rightAxisLines,
      }),
    [leftAxisBars, rightAxisBars, leftAxisLines, rightAxisLines],
  );

  const timeValues = useMemo(
    () =>
      getTimeValues(
        series,
        markers.map((marker) => marker.isoDate),
        xAxisTimeGap,
      ),
    [series, markers, xAxisTimeGap],
  );

  const buildDefinition = useCallback(
    () =>
      buildTimeseriesNewDefinition({
        series,
        timeValues,
        markers,
        xAxisLabels,
        brandTheme,
        scale,
        leftAxisLabel,
        rightAxisLabel,
        skipDecimals,
        padding: timesScalePadding,
        displayLinesInFrontOfBars,
      }),
    [
      series,
      timeValues,
      markers,
      xAxisLabels,
      brandTheme,
      scale,
      leftAxisLabel,
      rightAxisLabel,
      skipDecimals,
      timesScalePadding,
      displayLinesInFrontOfBars,
    ],
  );

  const definition = useMemo(
    () =>
      defineChart({
        chart: buildDefinition,
        tooltip: { use: tooltip },
        // One tooltip for everything plotted at the hovered date.
        focus: "group-x",
        maxFocusDistance: Number.POSITIVE_INFINITY,
      }),
    [buildDefinition],
  );

  const legendGroups = useMemo(() => {
    const groups = new Map<string, LineLegendItem[]>();

    series.forEach((item: TimeseriesNewSeries) => {
      const key = item.group ?? "";
      groups.set(key, [
        ...(groups.get(key) ?? []),
        {
          label: item.label,
          color: seriesColour(item, brandTheme.mode),
          swatch: item.kind === "bar",
          strokeWidth: 2.5,
          opacity: item.muted ? MUTED_OPACITY : undefined,
        },
      ]);
    });

    return Array.from(groups.entries());
  }, [series, brandTheme.mode]);

  if (isLoading) {
    return <Loader minHeight={minHeight} stretch={stretch} />;
  }

  if (!isPopulatedArray(timeValues)) {
    return <NoData message={noDataMessage} stretch={stretch} height="auto" />;
  }

  const ariaLabel =
    title ??
    ([leftAxisLabel, rightAxisLabel].filter(Boolean).join(" and ") ||
      "Timeseries chart");

  if (exportMode) {
    return (
      <ChartExportSvg
        id={id}
        title={title}
        ariaLabel={ariaLabel}
        brandTheme={brandTheme}
        buildDefinition={buildDefinition}
        legend={legendGroups.flatMap(([, items]) => items)}
      />
    );
  }

  const legendRight =
    (legendPosition ??
      (containerWidth > LEGEND_RIGHT_MIN_WIDTH ? "right" : "bottom")) ===
    "right";

  const renderRows = (rows: TimeseriesNewRow[]) => {
    const kind = rows[0]?.kind;
    const axis = rows[0]?.axis;
    const renderer =
      kind === "bar"
        ? barsTooltipRenderer
        : axis === "right"
          ? rightAxisLinesTooltipRenderer
          : leftAxisLinesTooltipRenderer;
    const textColor = brandTheme.theme.foreground ?? "";

    if (renderer) {
      return renderer({
        textColor,
        isoDate: rows[0].isoDate,
        data: rows.map((row) => ({
          label: row.label,
          value: row.value,
          color: row.colour as Colour,
        })),
      });
    }

    return rows.map((row) => (
      <Flex
        key={`${row.seriesIndex}`}
        alignItems="center"
        gap={2}
        justifyContent="space-between"
      >
        <Flex alignItems="center" gap={2}>
          <chakra.span
            w="10px"
            h="10px"
            borderRadius="2px"
            flexShrink={0}
            bg={seriesColour(row, brandTheme.mode)}
          />
          <Text as="span">{row.label}</Text>
        </Flex>
        <Text as="span" fontWeight="semibold" pl={4}>
          {toNumberDisplayValue(row.value)}
        </Text>
      </Flex>
    ));
  };

  return (
    <ErrorBoundary>
      <Flex
        ref={measureRef}
        direction={legendRight ? "row" : "column"}
        gap={legendRight ? 5 : 3}
        width="100%"
        height="100%"
      >
        <BrandChart
          definition={definition}
          brandTheme={brandTheme}
          ariaLabel={ariaLabel}
          minHeight={minHeight}
          height={stretch ? undefined : minHeight}
          flexGrow={stretch ? 1 : undefined}
          renderTooltipBody={({ points }) => {
            const data = points.map((point) => point.datum);
            const rows = data.filter(isSeriesRow);
            const markerRows = data.filter(isMarkerRow);
            const isoDate = rows[0]?.isoDate ?? markerRows[0]?.isoDate;

            if (!isoDate) {
              return null;
            }

            const kinds = [
              rows.filter((row) => row.kind === "bar"),
              rows.filter((row) => row.kind === "line" && row.axis === "left"),
              rows.filter((row) => row.kind === "line" && row.axis === "right"),
            ].filter(isPopulatedArray);

            return (
              <Flex direction="column" gap={1} fontSize="sm" minW="180px">
                <Text fontWeight="semibold">{formatTimeValue(isoDate)}</Text>
                {kinds.map((kindRows, index) => (
                  <Fragment key={index}>{renderRows(kindRows)}</Fragment>
                ))}
                {markerRows.map((row, index) => (
                  <Box
                    key={index}
                    mt={1}
                    pt={1}
                    borderTop="1px solid"
                    borderColor="var(--ts-chart-tooltip-border-color, currentColor)"
                  >
                    {row.description}
                  </Box>
                ))}
              </Flex>
            );
          }}
        />

        {!disableLegend && (
          <Flex
            direction={legendRight ? "column" : "row"}
            // A wrapping column would spill a long legend into extra columns
            // past maxWidth (and out of the card); scroll it instead.
            flexWrap={legendRight ? "nowrap" : "wrap"}
            gap={legendRight ? 4 : 6}
            flexShrink={0}
            maxWidth={legendRight ? "320px" : undefined}
            minHeight={0}
            overflowY={legendRight ? "auto" : undefined}
            pr={legendRight ? 2 : undefined}
          >
            {legendGroups.map(([group, items]) => (
              <LineLegend
                key={group}
                title={group || undefined}
                items={items}
                direction={legendRight ? "column" : "row"}
              />
            ))}
            {customLegend.map((section) => (
              <Flex
                key={section.section}
                direction="column"
                gap={1}
                fontSize="sm"
                maxW="250px"
                minW={0}
                flexShrink={0}
              >
                <Text fontWeight="semibold">{section.section}</Text>
                {section.items.map((item, index) => (
                  <Box
                    key={index}
                    fontWeight={
                      item.type === "subheader" ? "semibold" : "normal"
                    }
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {item.label}
                  </Box>
                ))}
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </ErrorBoundary>
  );
};

const TimeseriesNew = byLook(BrandTimeseriesNew, LegacyTimeseriesNew);

export { TimeseriesNew };
